/**
 * XMLHttpRequest-based upload with stall detection.
 *
 * `fetch()` does not fire upload progress events, so it cannot distinguish
 * between a slow-but-progressing upload and a genuinely stuck socket. XHR
 * does — we reset an idle timer on every `upload.onprogress` event and
 * abort only when no bytes have moved for `stallTimeoutMs`.
 *
 * The total ceiling (`totalTimeoutMs`) is a backstop for pathological
 * cases where progress keeps trickling forever; it should be very large.
 */

export interface StalledUploadOptions {
  method: "POST" | "PUT";
  url: string;
  /** XHR-compatible body — Blob, File, FormData, ArrayBuffer, string, etc. */
  body: XMLHttpRequestBodyInit;
  /** User cancel signal. */
  abortSignal?: AbortSignal;
  /** Abort if no progress event fires for this long. Defaults to 60s. */
  stallTimeoutMs?: number;
  /** Absolute ceiling regardless of progress. Defaults to 30min. */
  totalTimeoutMs?: number;
}

export interface StalledUploadResponse {
  ok: boolean;
  status: number;
  statusText: string;
  responseText: string;
  getHeader(name: string): string | null;
}

export class UploadStalledError extends Error {
  constructor(public readonly stallMs: number) {
    super(`Upload stalled: no progress for ${stallMs}ms`);
    this.name = "UploadStalledError";
  }
}

export class UploadTimedOutError extends Error {
  constructor(public readonly totalMs: number) {
    super(`Upload timed out after ${totalMs}ms`);
    this.name = "UploadTimedOutError";
  }
}

export class UploadCancelledError extends Error {
  constructor() {
    super("Upload was cancelled");
    this.name = "UploadCancelledError";
  }
}

export class UploadNetworkError extends Error {
  constructor(method: string, url: string) {
    super(`Network error during ${method} ${url}`);
    this.name = "UploadNetworkError";
  }
}

const DEFAULT_STALL_MS = 60 * 1000;
const DEFAULT_TOTAL_MS = 30 * 60 * 1000;
const STALL_CHECK_INTERVAL_MS = 5 * 1000;

export function uploadWithStallDetection(
  opts: StalledUploadOptions,
): Promise<StalledUploadResponse> {
  const {
    method,
    url,
    body,
    abortSignal,
    stallTimeoutMs = DEFAULT_STALL_MS,
    totalTimeoutMs = DEFAULT_TOTAL_MS,
  } = opts;

  return new Promise((resolve, reject) => {
    if (abortSignal?.aborted) {
      reject(new UploadCancelledError());
      return;
    }

    const xhr = new XMLHttpRequest();
    let lastProgressAt = Date.now();
    let settled = false;

    const settle = (fn: () => void) => {
      if (settled) return;
      settled = true;
      clearInterval(stallInterval);
      clearTimeout(totalTimer);
      abortSignal?.removeEventListener("abort", onCancel);
      fn();
    };

    const stallInterval = setInterval(() => {
      if (settled) return;
      if (Date.now() - lastProgressAt > stallTimeoutMs) {
        settle(() => {
          try {
            xhr.abort();
          } catch {
            /* ignore */
          }
          reject(new UploadStalledError(stallTimeoutMs));
        });
      }
    }, STALL_CHECK_INTERVAL_MS);

    const totalTimer = setTimeout(() => {
      settle(() => {
        try {
          xhr.abort();
        } catch {
          /* ignore */
        }
        reject(new UploadTimedOutError(totalTimeoutMs));
      });
    }, totalTimeoutMs);

    const onCancel = () => {
      settle(() => {
        try {
          xhr.abort();
        } catch {
          /* ignore */
        }
        reject(new UploadCancelledError());
      });
    };

    abortSignal?.addEventListener("abort", onCancel, { once: true });

    const bumpProgress = () => {
      lastProgressAt = Date.now();
    };
    xhr.upload.addEventListener("progress", bumpProgress);
    xhr.upload.addEventListener("load", bumpProgress);

    xhr.addEventListener("load", () => {
      settle(() => {
        resolve({
          ok: xhr.status >= 200 && xhr.status < 300,
          status: xhr.status,
          statusText: xhr.statusText,
          responseText: xhr.responseText,
          getHeader: (name) => xhr.getResponseHeader(name),
        });
      });
    });

    xhr.addEventListener("error", () => {
      settle(() => reject(new UploadNetworkError(method, url)));
    });

    xhr.addEventListener("abort", () => {
      // We abort the XHR ourselves from stall/total/cancel paths and
      // settle there. If we land here without being settled, it was an
      // external abort we didn't cause — treat as cancel.
      settle(() => reject(new UploadCancelledError()));
    });

    xhr.open(method, url);
    xhr.send(body);
  });
}
